import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Send, Edit3, CheckCircle2, Phone, ChevronDown } from 'lucide-react';
import { categories } from '../data/products';
import { useT } from '../utils/i18n';
import { getRegionMeta } from '../utils/region';
// SMS-first ordering for contractors. The killer differentiator vs. national
// marketplaces: a contractor on a jobsite can't fill out forms on their
// phone. They text. This component lets them tap a few buttons, opens
// their native Messages app with a perfectly-formatted order pre-filled,
// and they hit send.
//
// Cross-platform sms: link format — using `?&body=` is the most portable
// across iOS Safari / Android Chrome. Number is set to the dispatch line.
const DISPATCH_NUMBER = '+12066193742';
// Build the flat list of materials for the dropdown.
const ALL_MATERIALS = categories.flatMap(c => c.products.map(p => p.name));
const YARD_OPTIONS = [5, 7, 10, 12, 15, 20, 24, 36];
export function SMSOrderCTA() {
  const t = useT();
  const brandCompact = `${getRegionMeta().code}Gravel`;
  const [isOpen, setIsOpen] = useState(false);
  const [material, setMaterial] = useState('5/8" Minus');
  const [yards, setYards] = useState(12);
  const [address, setAddress] = useState('');
  const [timing, setTiming] = useState('asap');
  const [notes, setNotes] = useState('');
  const PRESETS = [{
    label: '12 yd 5/8 Minus',
    material: '5/8" Minus',
    yards: 12
  }, {
    label: '12 yd 1.25 Minus',
    material: '1.25" Minus',
    yards: 12
  }, {
    label: '12 yd 5/8 Clear',
    material: '5/8" Crushed Clear',
    yards: 12
  }, {
    label: '12 yd 1.5" Drain Rock',
    material: '1.5" Drain Rock',
    yards: 12
  }, {
    label: '15 yd Topsoil',
    material: '3-Way Topsoil',
    yards: 15
  }];
  const TIMING_OPTIONS = [{
    value: 'asap',
    label: t('sms.timing.asap')
  }, {
    value: 'today-am',
    label: t('sms.timing.todayAm')
  }, {
    value: 'today-pm',
    label: t('sms.timing.todayPm')
  }, {
    value: 'tomorrow-am',
    label: t('sms.timing.tomorrowAm')
  }, {
    value: 'tomorrow-pm',
    label: t('sms.timing.tomorrowPm')
  }, {
    value: 'this-week',
    label: t('sms.timing.thisWeek')
  }];
  const message = useMemo(() => {
    const timingLabel = TIMING_OPTIONS.find(opt => opt.value === timing)?.label || 'ASAP';
    const lines = [`${brandCompact} order:`, `${yards} yd ${material}`, address ? `Drop: ${address}` : 'Drop: [address]', `When: ${timingLabel}`];
    if (notes.trim()) lines.push(`Notes: ${notes.trim()}`);
    return lines.join('\n');
  }, [material, yards, address, timing, notes, TIMING_OPTIONS]);
  const smsHref = `sms:${DISPATCH_NUMBER}?&body=${encodeURIComponent(message)}`;
  const applyPreset = (preset: (typeof PRESETS)[number]) => {
    setMaterial(preset.material);
    setYards(preset.yards);
  };
  // Shared input classes — light mode uses white bg with stone borders;
  // dark mode keeps the original stone-800 panel look.
  const inputClass = 'w-full bg-white dark:bg-stone-800 border border-stone-300 dark:border-stone-700 text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-stone-500 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#fbbf24]/50 focus:border-[#fbbf24]/50 transition-all';
  const labelClass = 'block text-[10px] font-bold uppercase tracking-[0.15em] text-stone-600 dark:text-stone-400 mb-1.5';
  return <section className="bg-stone-100 dark:bg-[#1f1f1f] relative z-10 border-y border-[#fbbf24]/30 dark:border-[#fbbf24]/20" data-id="element-1756" data-mp="err7w2ojx2mg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1757" data-mp="4vedt3p3gmr6">
        {/* Always-visible pull-down header */}
        <button type="button" onClick={() => setIsOpen(v => !v)} aria-expanded={isOpen} aria-controls="sms-order-body" className="w-full flex items-center gap-4 sm:gap-6 py-5 sm:py-6 text-left group" data-id="element-1758" data-mp="v44pi609g83">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#fbbf24]/20 dark:bg-[#fbbf24]/15 border border-[#fbbf24]/40 dark:border-[#fbbf24]/30 flex items-center justify-center shrink-0" data-id="element-1759" data-mp="1v9exg1pq0uj5">
            <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-[#b45309] dark:text-[#fbbf24]" data-id="element-1760" data-mp="279llx1dqcdim" />
          </div>
          <div className="min-w-0 flex-1" data-id="element-1761" data-mp="1ta3tmc186i4i">
            <div className="inline-flex items-center gap-2 mb-1" data-id="element-1762" data-mp="1xcgxeuwew5yn">
              <span className="w-1.5 h-1.5 rounded-full bg-[#fbbf24] animate-pulse" data-id="element-1763" data-mp="1xaw5ld1hf14h" />
              <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#b45309] dark:text-[#fbbf24]" data-id="element-1764" data-mp="1jkaifc4zw50s">
                {t('sms.header.eyebrow')}
              </span>
            </div>
            <p className="font-heading text-lg sm:text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight leading-tight" data-id="element-1765" data-mp="19o7f42yeq0c9">
              {t('sms.header.title1')}
              <span className="text-[#b45309] dark:text-[#fbbf24]" data-id="element-1766" data-mp="10ly6jimp8h1i">
                {t('sms.header.title2')}
              </span>
            </p>
            <p className="hidden sm:block text-xs text-stone-600 dark:text-stone-400 mt-1" data-id="element-1767" data-mp="15khshj1alstv">
              {isOpen ? t('sms.header.subtitleOpen') : t('sms.header.subtitleClosed')}
            </p>
          </div>
          <motion.div animate={{
          rotate: isOpen ? 180 : 0
        }} transition={{
          duration: 0.25,
          ease: 'easeInOut'
        }} className="shrink-0 w-9 h-9 rounded-full border border-stone-300 dark:border-stone-700 group-hover:border-[#fbbf24]/50 flex items-center justify-center text-stone-700 dark:text-stone-300 group-hover:text-[#fbbf24] transition-colors" data-id="element-1768" data-mp="h2ircukk1aa8">
            <ChevronDown className="w-5 h-5" data-id="element-1769" data-mp="zx692spo72h6" />
          </motion.div>
        </button>

        {/* Collapsible body */}
        <AnimatePresence initial={false} data-id="element-1770" data-mp="19p3k9zgohufw">
          {isOpen && <motion.div key="sms-body" id="sms-order-body" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.35,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} className="overflow-hidden" data-id="element-1771" data-mp="19qen9x2auy5y">
              <div className="pt-2 pb-16 lg:pb-20" data-id="element-1772" data-mp="lankcrj2176p">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-id="element-1773" data-mp="l9chqv1ci5h6">
                  {/* Pitch */}
                  <motion.div initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.05
              }} className="lg:col-span-5" data-id="element-1774" data-mp="u3ii706700mg">
                    <h3 className="font-heading text-3xl md:text-4xl font-extrabold text-stone-900 dark:text-white mb-5 leading-[1.1] tracking-tight" data-id="element-1775" data-mp="190hvn4nq6nou">
                      {t('sms.pitch.title1')} <br data-id="element-1776" data-mp="1d0a47hd4ctu7" />
                      <span className="text-[#b45309] dark:text-[#fbbf24]" data-id="element-1777" data-mp="3aswd62mcxq4q">
                        {t('sms.pitch.title2')}
                      </span>
                    </h3>
                    <p className="text-stone-700 dark:text-stone-300 text-base md:text-lg leading-relaxed mb-7 max-w-md" data-id="element-1778" data-mp="37agoyuzwss4v">
                      {t('sms.pitch.desc')}
                    </p>
                    <div className="space-y-3 max-w-md" data-id="element-1779" data-mp="32hx9ttlkpoxy">
                      {[t('sms.pitch.bullet1'), t('sms.pitch.bullet2'), t('sms.pitch.bullet3')].map(line => <div key={line} className="flex items-start gap-3" data-id="element-1780" data-mp="2tk3hetqxjfg2">
                          <CheckCircle2 className="w-5 h-5 text-[#b45309] dark:text-[#fbbf24] shrink-0 mt-0.5" data-id="element-1781" data-mp="3q9umto0ren0n" />
                          <span className="text-stone-700 dark:text-stone-200 text-sm" data-id="element-1782" data-mp="3l9zxhowmu5hp">
                            {line}
                          </span>
                        </div>)}
                    </div>
                    <div className="mt-7 flex items-center gap-3 text-sm text-stone-600 dark:text-stone-400" data-id="element-1783" data-mp="3c8i8fh18ytgv">
                      <Phone className="w-4 h-4" data-id="element-1784" data-mp="25u94kc6m1qvt" />
                      <span data-id="element-1785" data-mp="20uehkjpyhlg1">
                        {t('sms.pitch.call')}{' '}
                        <a href={`tel:${DISPATCH_NUMBER}`} className="text-stone-900 dark:text-white font-semibold hover:underline" data-id="element-1786" data-mp="20wr5ddieaq10">
                          (206) 619-3742
                        </a>
                      </span>
                    </div>
                  </motion.div>

                  {/* Builder card — looks like a phone */}
                  <motion.div initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.4,
                delay: 0.1
              }} className="lg:col-span-7" data-id="element-1787" data-mp="3vwrn670a7uiw">
                    <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-stone-700 shadow-2xl overflow-hidden" data-id="element-1788" data-mp="dqwr5orny8yj">
                      {/* Header */}
                      <div className="flex items-center justify-between px-5 py-4 bg-stone-50 dark:bg-[#333] border-b border-stone-200 dark:border-stone-700" data-id="element-1789" data-mp="16gc1sdff1ztx">
                        <div className="flex items-center gap-3" data-id="element-1790" data-mp="1b956x2esra6j">
                          <div className="w-10 h-10 rounded-full bg-[#fbbf24]/20 dark:bg-[#fbbf24]/15 flex items-center justify-center" data-id="element-1791" data-mp="2vwfyjk4jnm5x">
                            <MessageSquare className="w-5 h-5 text-[#b45309] dark:text-[#fbbf24]" data-id="element-1792" data-mp="2qwtkx9kt03wl" />
                          </div>
                          <div data-id="element-1793" data-mp="30o7hp9ldepm1">
                            <p className="text-stone-900 dark:text-white font-bold text-sm" data-id="element-1794" data-mp="30ozcwjb22lbs">
                              {`${brandCompact} Dispatch`}
                            </p>
                            <p className="text-stone-600 dark:text-stone-400 text-[11px]" data-id="element-1795" data-mp="280tq7r0fym9b">
                              {t('sms.builder.replies')}
                            </p>
                          </div>
                        </div>
                        <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-bold text-stone-700 dark:text-stone-300 bg-stone-200 dark:bg-stone-700/50 px-2 py-1 rounded" data-id="element-1796" data-mp="2h2tb4uekuahj">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#04A05C] animate-pulse" data-id="element-1797" data-mp="3shq1c7mhz5qh" />
                          {t('sms.builder.online')}
                        </span>
                      </div>

                      {/* Builder */}
                      <div className="p-5 sm:p-6 space-y-5" data-id="element-1798" data-mp="54kaifk3twts">
                        {/* Presets */}
                        <div data-id="element-1799" data-mp="14jxmzcnltou">
                          <label className={labelClass + ' mb-2'} data-id="element-1800" data-mp="138sow7h2fw5">
                            {t('sms.builder.quickOrders')}
                          </label>
                          <div className="flex flex-wrap gap-2" data-id="element-1801" data-mp="3nap9dlyv9k19">
                            {PRESETS.map(preset => {
                          const active = material === preset.material && yards === preset.yards;
                          return <button key={preset.label} type="button" onClick={() => applyPreset(preset)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active ? 'bg-[#fbbf24] text-stone-900 border-[#fbbf24]' : 'bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-700 hover:border-[#fbbf24]/60 dark:hover:border-[#fbbf24]/40 hover:text-stone-900 dark:hover:text-white'}`} data-id="element-1802" data-mp="3ebloqdk1wwkn">
                                  {preset.label}
                                </button>;
                        })}
                          </div>
                        </div>

                        {/* Custom builder */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1803" data-mp="2bipxr8qk9has">
                          <div data-id="element-1804" data-mp="2gg9a233p7k03">
                            <label className={labelClass} data-id="element-1805" data-mp="2ghkf068vqxss">
                              {t('sms.builder.material')}
                            </label>
                            <select value={material} onChange={e => setMaterial(e.target.value)} className={inputClass} data-id="element-1806" data-mp="2ph7h6u9fo7mw">
                              {ALL_MATERIALS.map(m => <option key={m} value={m} data-id="element-1807" data-mp="3vwq8ludar00c">
                                  {m}
                                </option>)}
                            </select>
                          </div>
                          <div data-id="element-1808" data-mp="20ss64at6ovwg">
                            <label className={labelClass} data-id="element-1809" data-mp="20w7pvqxsyt47">
                              {t('sms.builder.yards')}
                            </label>
                            <select value={yards} onChange={e => setYards(parseInt(e.target.value))} className={inputClass} data-id="element-1810" data-mp="2arho03l2ygcp">
                              {YARD_OPTIONS.map(n => <option key={n} value={n} data-id="element-1811" data-mp="25rmx401b1qrn">
                                  {n} yd
                                </option>)}
                            </select>
                          </div>
                        </div>

                        <div data-id="element-1812" data-mp="1rtyd0uf2annn">
                          <label className={labelClass} data-id="element-1813" data-mp="1rv9l36fjihkk">
                            {t('sms.builder.dropAddress')}
                          </label>
                          <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder={t('sms.builder.addressPlaceholder')} className={inputClass} data-id="element-1814" data-mp="zcuflqo1wlyd" />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1815" data-mp="361eaqxm9twvp">
                          <div data-id="element-1816" data-mp="3as328cxyatuu">
                            <label className={labelClass} data-id="element-1817" data-mp="3ateez29djby3">
                              {t('sms.builder.when')}
                            </label>
                            <select value={timing} onChange={e => setTiming(e.target.value)} className={inputClass} data-id="element-1818" data-mp="2wz51sc6nibkf">
                              {TIMING_OPTIONS.map(tOpt => <option key={tOpt.value} value={tOpt.value} data-id="element-1819" data-mp="3ot73qs1cbi3f">
                                  {tOpt.label}
                                </option>)}
                            </select>
                          </div>
                          <div data-id="element-1820" data-mp="3fr6ae6989g71">
                            <label className={labelClass} data-id="element-1821" data-mp="3kqag2mwy0vsm">
                              {t('sms.builder.notes')}
                            </label>
                            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('sms.builder.notesPlaceholder')} className={inputClass} data-id="element-1822" data-mp="24apobu3641b7" />
                          </div>
                        </div>

                        {/* Preview bubble */}
                        <div className="rounded-xl bg-stone-50 dark:bg-stone-900/60 border border-stone-200 dark:border-stone-700/50 p-4" data-id="element-1823" data-mp="2jt945a9ynlr5">
                          <div className="flex items-center justify-between mb-2" data-id="element-1824" data-mp="2et4p7oopaz31">
                            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-600 dark:text-stone-400 inline-flex items-center gap-1.5" data-id="element-1825" data-mp="2avgog50buc38">
                              <Edit3 className="w-3 h-3" data-id="element-1826" data-mp="32kjqde4qracp" />
                              {t('sms.builder.preview')}
                            </span>
                            <span className="text-[10px] text-stone-500" data-id="element-1827" data-mp="2ylax0adm5eho">
                              {t('sms.builder.to')} {DISPATCH_NUMBER}
                            </span>
                          </div>
                          <pre className="font-sans text-sm text-stone-800 dark:text-stone-100 whitespace-pre-wrap leading-relaxed" data-id="element-1828" data-mp="1i4ybmc9swwcm">
                            {message}
                          </pre>
                        </div>

                        {/* Send */}
                        <a href={smsHref} className="group flex items-center justify-center gap-2 w-full bg-[#fbbf24] hover:bg-[#f59e0b] text-stone-900 font-bold py-4 rounded-xl text-base transition-colors shadow-[0_4px_20px_rgba(251,191,36,0.25)]" data-id="element-1829" data-mp="2m8syyj4jmpr">
                          <Send className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" data-id="element-1830" data-mp="gdx34w9tc143" />
                          {t('sms.builder.openMessages')}
                        </a>
                        <p className="text-[11px] text-stone-500 text-center" data-id="element-1831" data-mp="qcmnqwt3fzo1">
                          {t('sms.builder.finePrint')}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}